import { APIClient, ApiQueryParams, DefaultQueryParams } from '@frontend/api-utils';
import { EntityType } from '@frontend/common';
import {
    CreateGroup,
    CreateGroupMember,
    GroupListResponse,
    GroupMember,
    GroupMemberListResponse,
    GroupMemberQueryParams,
    GroupQueryParams,
    IGroup,
    UpdateGroup,
    UpdateGroupMember
} from '@frontend/group/types';

export class UserGroupClient extends APIClient {
    public static readonly endpoint = '/group-api/v1';

    public static async fetchUserGroups(queryParams?: ApiQueryParams<GroupQueryParams>): Promise<GroupListResponse> {
        return await this.apiPaginated<GroupListResponse, GroupQueryParams>(`${this.endpoint}/user-groups`, queryParams, undefined);
    }

    public static async resolveUserGroups(ids: string[], field: string = 'id'): Promise<GroupListResponse> {
        return this.resolve<GroupListResponse>(`${this.endpoint}/user-groups`, ids, field);
    }

    public static async deleteUserGroups(groupIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/user-groups-delete`, { ids: groupIds });
        return await this.handleVoidResponse(response);
    }

    public static async postUserGroup(accountId: string, group: CreateGroup): Promise<IGroup> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/user-groups`, group);
        return await this.handleResponse<IGroup>(response);
    }

    public static async fetchUserGroup(accountId: string, groupId: string): Promise<IGroup> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/user-groups/${groupId}`);
        return await this.handleResponse<IGroup>(response);
    }

    public static async patchUserGroup(accountId: string, groupId: string, group: UpdateGroup): Promise<IGroup> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/user-groups/${groupId}`, group);
        return await this.handleResponse<IGroup>(response);
    }

    public static async deleteUserGroup(accountId: string, groupId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/user-groups/${groupId}`);
        return await this.handleVoidResponse(response);
    }

    public static async fetchGroupsNotInGroup(
        accountId: string,
        groupId: string,
        queryParams?: ApiQueryParams<DefaultQueryParams | GroupQueryParams>
    ): Promise<GroupListResponse> {
        return await this.apiPaginated<GroupListResponse, DefaultQueryParams & GroupQueryParams>(
            `${this.endpoint}/accounts/${accountId}/user-groups/${groupId}/groups`,
            queryParams
        );
    }

    //IGroup members
    public static async fetchUserGroupMembers(queryParams?: ApiQueryParams<GroupMemberQueryParams>): Promise<GroupMemberListResponse> {
        return await this.apiPaginated<GroupMemberListResponse, GroupMemberQueryParams>(`${this.endpoint}/user-group-members`, queryParams);
    }

    public static async resolveUserGroupMembers(ids: string[], field: string = 'id'): Promise<GroupMemberListResponse> {
        return this.resolve<GroupMemberListResponse>(`${this.endpoint}/user-group-members`, ids, field);
    }

    public static async deleteUserGroupMembers(memberIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/user-group-members-delete`, { ids: memberIds });
        return await this.handleVoidResponse(response);
    }

    public static async postUserGroupMember(accountId: string, groupId: string, groupMember: CreateGroupMember): Promise<GroupMember> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/user-groups/${groupId}/members`, groupMember);
        return await this.handleResponse<GroupMember>(response);
    }

    public static async postUserGroupMemberList(accountId: string, groupId: string, entity_type: EntityType, memberIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/user-groups/${groupId}/${entity_type}s/members-list`, { ids: memberIds });
        return await this.handleVoidResponse(response);
    }

    public static async fetchUserGroupMember(accountId: string, groupId: string, memberId: string): Promise<GroupMember> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/user-groups/${groupId}/members/${memberId}`);
        return await this.handleResponse<GroupMember>(response);
    }

    public static async patchUserGroupMember(accountId: string, groupId: string, memberId: string, body: UpdateGroupMember): Promise<GroupMember> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/user-groups/${groupId}/members/${memberId}`, body);
        return await this.handleResponse<GroupMember>(response);
    }

    public static async deleteUserGroupMember(accountId: string, groupId: string, memberId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/user-groups/${groupId}/members/${memberId}`);
        return await this.handleVoidResponse(response);
    }
}
